import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectLimitsModalData } from '~/components/account/ducks';
import { NOTIFICATION_TYPE, showNativeNotification } from '~/components/app-bridge';
import { isNativeNotificationEnabled } from '~/components/app-bridge/app-properties';
import { selectHasRegulationsHeader } from '~/components/header/regulations-header';
import { showQueuedNotification } from '~/components/notifications/ducks/actions';
import { Notification } from '~/components/notifications/ducks/types';
import useTranslations from '~/components/translation/use-translations';
import { RootState } from '~/store/types';
import useUIContext from '~/ui/use-ui-context';

import { textKeys } from '../constants';
import { FixedContainer, SidebarHeaderContainer } from '../styled';
import SpendingLimitContent from './content';
import useEventSource from './hooks/useEventSource';
import useIframeLoader from './hooks/useIframeLoader';
import UploadSpendingDocument from './upload-docs';
import { NOTIFICATION_ID, USER_FLOW_COMPLETED } from './utils/constant';
import { trackSpendingLimitEvent } from './utils/tracking';

const SpendingLimitModal = () => {
	const dispatch = useDispatch();

	const [isDisabled, setisDisabled] = useState(false);
	const [uploadDoc, setUploadDoc] = useState(false);

	const hasRegulationHeader = useSelector<RootState, boolean>(selectHasRegulationsHeader);
	const { meta } = useSelector(selectLimitsModalData) ?? {};
	const { translations } = useTranslations(textKeys);
	const { iframeLoaded, handleIframeLoad, handleIframeUnload } = useIframeLoader();
	const uiContext = useUIContext();

	const { withinSidebar } = uiContext;

	const handleNotification = useCallback(
		(notificationProps: Partial<Notification>, option = {}) => {
			if (isNativeNotificationEnabled()) {
				const type = notificationProps.buttonText
					? NOTIFICATION_TYPE.SPENDING_LIMIT_WITH_DEPOSIT_BUTTON
					: NOTIFICATION_TYPE.SPENDING_LIMIT;
				if (notificationProps.message) {
					showNativeNotification(type, notificationProps.message);
				}
			} else {
				dispatch(
					showQueuedNotification({
						autoHide: true,
						openInSidebar: withinSidebar,
						id: NOTIFICATION_ID,
						...notificationProps
					})
				);
			}
			handleIframeUnload(option);
		},
		[dispatch, handleIframeUnload, withinSidebar]
	);

	const [setIsEventSourceActive] = useEventSource(handleNotification);

	const messageHandler = useCallback(
		({ data: rawData }: { data: string | object }) => {
			const data = (typeof rawData === 'string' ? JSON.parse(rawData) : rawData) as {
				status: string;
				message: string;
			};

			const { status, message } = data;
			if (status === USER_FLOW_COMPLETED.status) {
				if (message === USER_FLOW_COMPLETED.bankingAuthStarted) {
					setisDisabled(true);
				} else if (message === USER_FLOW_COMPLETED.userFlowCompleted) {
					setIsEventSourceActive(true);
				}
			}
		},
		[setisDisabled, setIsEventSourceActive]
	);

	useEffect(() => {
		if (iframeLoaded) {
			window.addEventListener('message', messageHandler);
		}
		return () => {
			window.removeEventListener('message', messageHandler);
		};
	}, [iframeLoaded, messageHandler]);

	useEffect(() => {
		trackSpendingLimitEvent({ label: 'Pop-up displayed' });
	}, []);

	const onVerify = () => {
		trackSpendingLimitEvent({ label: 'Click verify with bank' });
		handleIframeLoad();
	};

	const onUploadDoc = () => {
		trackSpendingLimitEvent({ label: 'Click upload docs' });
		setUploadDoc(true);
	};

	const onClose = () => {
		trackSpendingLimitEvent({ label: 'Click close' });
		handleIframeUnload();
	};

	if (uploadDoc) {
		return (
			<FixedContainer hasRegulationHeader={hasRegulationHeader} uiContext={uiContext}>
				<UploadSpendingDocument onClose={() => setUploadDoc(false)} onComplete={() => handleIframeUnload()} />
			</FixedContainer>
		);
	}

	if (iframeLoaded) {
		return (
			<FixedContainer hasRegulationHeader={hasRegulationHeader} uiContext={uiContext}>
				{/* @ts-expect-error - $FixMe SidebarHeader is js file */}
				<SidebarHeaderContainer isDisabled={isDisabled} closeIcon onClose={onClose}>
					<h2 className="js-title page-head__title">{translations.MOBILE_KREDITZ_TITLE}</h2>
					<div className="page-head__right-side" />
				</SidebarHeaderContainer>
				<iframe src={meta?.iframeUrl} title="Spending Limit Iframe" onLoad={handleIframeLoad}>
					{translations.DIALOG_LOADING}
				</iframe>
			</FixedContainer>
		);
	}

	return <SpendingLimitContent onVerifyWithBank={onVerify} onUploadDocuments={onUploadDoc} onClose={onClose} />;
};

export default SpendingLimitModal;
